import { useState } from 'react'
import { useModals } from 'almanac-ui/Modal'
import { AnalysingModal } from '../SourceMaterialModal/AnalysingModal'
import { SourcePreviewModal } from '../SourceMaterialModal/SourcePreviewModal'
import { useIsUrlCrawlable } from 'hooks/useIsUrlCrawlable'
import {
  useCreateSourceSync,
  SourceMaterialScrapingJobFailureError,
} from 'hooks/queries/workspaces/useCreateSourceSync'
import { useCreateMediaObjects } from 'hooks/queries/mediaObjects/useCreateMediaObjects'
import { useCreateSourceMaterial } from 'hooks/useSourceMaterials'
import { useCreateBrandKitSource } from 'hooks/queries/useCreateBrandKitSource'
import { CONTENT_CHAR_LIMIT, type ScreenType } from './constants'
import { normalizeUrl } from 'flows/common/steps/BrandKitForm/utils'
import { showErrorMessageToast } from 'common/MessageToasts/ErrorMessageToast/ErrorMessageToast'
import { AuthorizationError } from 'utils/error'
import type {
  BrandKitSource,
  KnowledgeBaseSourceMaterial,
  PlainText,
  Website,
  AlmanacFileSource,
  SourceMaterialsMediaObject,
  Source,
} from 'types'

type SourceMaterialWithUrl = KnowledgeBaseSourceMaterial & { source_url: string }

const isWebsite = (source: Source): source is Website => source.type === 'Website'
const isPlainText = (source: Source): source is PlainText => source.type === 'PlainText'
const isSourceMaterialsMediaObject = (source: Source): source is SourceMaterialsMediaObject =>
  source.type === 'SourceMaterialsMediaObject'

interface SubmissionInputs {
  url: string
  text: string
  file: File | null
  fileContent: string
  name: string
  scrapeImages?: boolean
  followLinks?: boolean
}

interface UseSourceMaterialModalParams {
  currentScreen: ScreenType | null
  brandKitId?: number
  onHandleSourceMaterialSuccess?: (sourceMaterial: KnowledgeBaseSourceMaterial) => void
  onBrandKitSourceSuccess?: (brandKitSource: BrandKitSource) => void
  onSuccess?: (
    source: PlainText | Website | AlmanacFileSource | SourceMaterialsMediaObject,
    fileName?: string,
  ) => void | Promise<void>
  onError?: () => void
}

export const useSourceMaterialModal = ({
  currentScreen,
  brandKitId,
  onHandleSourceMaterialSuccess,
  onBrandKitSourceSuccess,
  onSuccess,
  onError,
}: UseSourceMaterialModalParams) => {
  const { openModal, closeModal } = useModals()
  const { mutateAsync: createSourceSync } = useCreateSourceSync()
  const { mutateAsync: createMediaObjects } = useCreateMediaObjects()
  const { mutateAsync: createSourceMaterial } = useCreateSourceMaterial()
  const { mutateAsync: createBrandKitSource } = useCreateBrandKitSource()
  const [isSubmitting, setIsSubmitting] = useState(false)

  const [url, setUrl] = useState('')
  const [text, setText] = useState('')
  const [fileContent, setFileContent] = useState('')
  const [name, setName] = useState('')

  const { isValid: isValidUrl } = useIsUrlCrawlable(url)

  const nameRequired = !!brandKitId
  const isNameValid = !nameRequired || name.length > 0

  const isValid =
    (currentScreen === 'website' && url.length > 0 && isValidUrl) ||
    (currentScreen === 'text' && text.length > 0 && isNameValid) ||
    (currentScreen === 'file' && fileContent.length > 0)

  const submitWithBrandKit = async (screen: ScreenType, inputs: SubmissionInputs) => {
    if (!brandKitId || !onBrandKitSourceSuccess) return

    let brandKitSource: BrandKitSource | undefined

    if (screen === 'website') {
      const websiteName = new URL(inputs.url).hostname.replace(/^www\./, '')
      brandKitSource = await createBrandKitSource({
        brandKitId,
        sourceType: 'Website',
        url: inputs.url,
        name: websiteName,
        scrapeImages: inputs.scrapeImages,
        followLinks: inputs.followLinks,
      })
    } else if (screen === 'text') {
      brandKitSource = await createBrandKitSource({
        brandKitId,
        sourceType: 'PlainText',
        text: inputs.text,
        name: inputs.name,
      })
    } else if (screen === 'file' && inputs.file) {
      const mediaObjects = await createMediaObjects([{ file: inputs.file, filename: inputs.file.name }])
      brandKitSource = await createBrandKitSource({
        brandKitId,
        sourceType: 'SourceMaterialsMediaObject',
        mediaObjectId: mediaObjects[0].id,
        name: inputs.file.name,
      })
    }

    if (brandKitSource) {
      closeModal()
      onBrandKitSourceSuccess(brandKitSource)
    }
  }

  const submitWithCallback = async (screen: ScreenType, inputs: SubmissionInputs) => {
    if (screen === 'website') {
      const data = await createSourceSync({
        type: 'Website',
        url: inputs.url,
        scrape_images: inputs.scrapeImages,
        follow_links: inputs.followLinks,
      })
      if (isWebsite(data.source)) {
        onSuccess?.(data.source)
      }
    } else if (screen === 'text') {
      const data = await createSourceSync({ type: 'PlainText', text: inputs.text })
      if (isPlainText(data.source)) {
        onSuccess?.(data.source)
      }
    } else if (screen === 'file' && inputs.file) {
      const mediaObjects = await createMediaObjects([{ file: inputs.file, filename: inputs.file.name }])
      // Our file picker only allows one file at a time
      const data = await createSourceSync({ type: 'SourceMaterialsMediaObject', media_object_id: mediaObjects[0].id })
      if (isSourceMaterialsMediaObject(data.source)) {
        await onSuccess?.(data.source, inputs.file.name)
      }
    }
  }

  const submitWithModal = async (screen: ScreenType, inputs: SubmissionInputs) => {
    if (!onHandleSourceMaterialSuccess) return

    if (screen === 'website') {
      const sourceMaterial: SourceMaterialWithUrl = {
        source_url: inputs.url,
      } as SourceMaterialWithUrl

      openModal(AnalysingModal, {
        onError,
        onHandleSourceMaterialSuccess,
        sourceMaterial,
      })
    } else if (screen === 'text') {
      openModal(SourcePreviewModal, {
        sourceMaterial: {
          name: '',
          emoji: null,
          color: null,
          content: inputs.text.substring(0, CONTENT_CHAR_LIMIT),
        },
        onHandleSourceMaterialSuccess,
      })
    } else if (screen === 'file' && inputs.file) {
      const result = await createSourceMaterial({
        name: inputs.file.name,
        emoji: null,
        color: null,
        content: inputs.fileContent.substring(0, CONTENT_CHAR_LIMIT),
        source_file: inputs.file,
      })
      closeModal()
      onHandleSourceMaterialSuccess(result)
    }
  }

  const submit = async (screen: ScreenType | null, inputs: SubmissionInputs) => {
    if (!screen) return

    const normalizedInputs: SubmissionInputs = {
      ...inputs,
      url: inputs.url ? normalizeUrl(inputs.url) : inputs.url,
    }

    setIsSubmitting(true)
    try {
      if (brandKitId && onBrandKitSourceSuccess) {
        await submitWithBrandKit(screen, normalizedInputs)
      } else if (onSuccess) {
        await submitWithCallback(screen, normalizedInputs)
      } else if (onHandleSourceMaterialSuccess) {
        await submitWithModal(screen, normalizedInputs)
      }
    } catch (e) {
      if (e instanceof SourceMaterialScrapingJobFailureError) {
        showErrorMessageToast(e.message)
      } else if (e instanceof AuthorizationError) {
        showErrorMessageToast(e.message)
      } else {
        showErrorMessageToast('An error occurred! Our team has been notified and we are looking into it.')
      }
      onError?.()
    } finally {
      setIsSubmitting(false)
    }
  }

  return {
    url,
    setUrl,
    text,
    setText,
    fileContent,
    setFileContent,
    name,
    setName,
    isValid,
    isSubmitting,
    submit,
  }
}
